import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import CallButton from '../components/CallButton';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { IMAGE_ASSETS } from '../constants/images';
import { eventsData } from '../data/eventsData';
import { getEventStatus } from '../utils/eventHelpers';
import { format, parseISO } from 'date-fns';
import { useLanguage } from '../context/LanguageContext';

const { FiStar, FiArrowRight, FiChevronRight } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const SectionHeader = ({ title, subtitle, light = false }) => (
  <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="text-center mb-12">
    <h2 className={`text-4xl font-bold mb-4 ${light ? 'text-white' : 'text-brand-navy'}`}>{title}</h2>
    <div className="h-1 w-20 bg-brand-red mx-auto rounded mb-4"></div>
    {subtitle && <p className={`text-lg max-w-2xl mx-auto ${light ? 'text-gray-300' : 'text-gray-600'}`}>{subtitle}</p>}
  </motion.div>
);

export default function Home() {
  const { t, lang } = useLanguage();
  
  const featuredEvents = eventsData
    .filter(e => getEventStatus(e.startDate, e.endDate).label !== 'Expired')
    .slice(0, 3);

  return (
    <div className="w-full">
      <section className="relative h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden bg-brand-navy">
        <div className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-overlay" style={{ backgroundImage: `url("/assets/images/hero.jpg")` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-transparent to-transparent opacity-80" />
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <span className="text-brand-gold font-semibold tracking-widest uppercase mb-4 block">{t('home.hero.welcome')}</span>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 text-shadow-md">Johnny's Bar Billard</h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto font-light leading-relaxed">{t('home.hero.subtitle')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <CallButton className="text-lg px-8 py-4 w-full sm:w-auto">
                {t('common.reserve')}
              </CallButton>
              <Link to="/menu" className="w-full sm:w-auto">
                <button className="w-full bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all">
                  {t('home.hero.explore')}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:w-1/2 relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-8 border-white">
                <img src={IMAGE_ASSETS.aboutStory} alt="About Johnny's" className="w-full h-[500px] object-cover" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:w-1/2">
              <span className="text-brand-red font-bold uppercase tracking-widest mb-4 block">{t('home.about.since')}</span>
              <h2 className="text-4xl font-bold text-brand-navy mb-6">{t('home.about.title')}</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">{t('home.about.text')}</p>
              <Link to="/about">
                <button className="bg-brand-navy text-white hover:bg-brand-red px-8 py-3 rounded-full font-semibold transition-all duration-300">
                  {t('common.learnMore')}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50 border-y border-gray-100">
        <div className="container mx-auto px-4">
          <SectionHeader title={t('home.menu.title')} subtitle={t('home.menu.subtitle')} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              { title: lang === 'en' ? 'The 8-Ball Burger' : 'Le Burger 8-Ball', price: '$18', img: IMAGE_ASSETS.burger },
              { title: lang === 'en' ? 'Signature Wings' : 'Ailes Signature', price: '$16', img: IMAGE_ASSETS.wings },
              { title: lang === 'en' ? 'Craft IPAs' : 'IPAs Artisanales', price: lang === 'en' ? 'From $9' : 'Dès 9 $', img: IMAGE_ASSETS.beer }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl overflow-hidden shadow-lg group border border-gray-100 text-center">
                <div className="relative h-48 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute top-4 right-4 bg-brand-navy text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">{item.price}</div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-brand-navy mb-2">{item.title}</h3>
                  <div className="flex justify-center text-brand-gold">
                    <SafeIcon icon={FiStar} /><SafeIcon icon={FiStar} /><SafeIcon icon={FiStar} /><SafeIcon icon={FiStar} /><SafeIcon icon={FiStar} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/menu">
              <button className="border-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300">
                {t('common.viewMenu')}
              </button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
            <div className="text-left">
              <span className="text-brand-red font-bold uppercase tracking-widest mb-2 block">{t('home.events.tag')}</span>
              <h2 className="text-4xl font-bold text-brand-navy">{t('home.events.title')}</h2>
            </div>
            <Link to="/pool-events" className="text-brand-navy font-bold hover:text-brand-red flex items-center gap-2 group">
              {t('common.viewAllEvents')} <SafeIcon icon={FiChevronRight} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="space-y-6">
            {featuredEvents.map((event, i) => {
              const status = getEventStatus(event.startDate, event.endDate);
              const statusLabel = lang === 'en' ? status.label : (status.label === 'Ongoing' ? 'En cours' : 'À venir');
              return (
                <motion.div key={event.id} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-gray-50 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center border border-gray-100 hover:border-brand-gold/30 hover:shadow-xl transition-all group" >
                  <div className="flex-shrink-0 w-32 h-32 bg-brand-navy rounded-2xl flex flex-col items-center justify-center text-white shadow-lg group-hover:bg-brand-red transition-colors">
                    <span className="text-3xl font-bold">{format(parseISO(event.startDate), 'dd')}</span>
                    <span className="text-sm font-medium uppercase tracking-tighter">{format(parseISO(event.startDate), 'MMM')}</span>
                  </div>
                  <div className="flex-grow text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                      <span className={`${status.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest`}>{statusLabel}</span>
                      <span className="text-brand-gold text-xs font-bold uppercase tracking-widest">{event.type[lang]}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-brand-navy mb-2">{event.title[lang]}</h3>
                    <p className="text-gray-500 max-w-xl">{event.description[lang]}</p>
                  </div>
                  <Link to="/pool-events" className="p-4 rounded-full bg-white border border-gray-200 text-brand-navy hover:bg-brand-navy hover:text-white transition-all">
                    <SafeIcon icon={FiArrowRight} className="text-xl" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-brand-navy rounded-[3rem] p-12 text-center text-white relative overflow-hidden shadow-2xl">
            <h2 className="text-4xl font-bold mb-6">{t('home.cta.title')}</h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">{t('home.cta.text')}</p>
            <CallButton className="px-10 py-4 text-lg">
              {t('common.reserve')}
            </CallButton>
          </div>
        </div>
      </section>
    </div>
  );
}
